import type { ConversationModel } from 'Models/ConversationModel';
import { ConversationMetadata, ConversationType, DeleteConversationModalContent } from '../types';
import { MIN_PARTICIPANTS_TO_ALLOW_LEAVE_GROUP } from 'Constants/env';

/**
 * Determines the conversation type and metadata based on conversation properties
 * @param conversation - The conversation model
 * @param conversationName - Optional conversation name (for one-to-one conversations)
 * @returns Conversation metadata including type and active status
 */
export const getConversationMetadata = (
  conversation: ConversationModel,
  conversationName?: string
): ConversationMetadata => {
  const isActiveGroup = conversation.isActiveParticipant;

  const conversationType: ConversationType =
    conversation.grouping === 'OneOnOne'
      ? 'one-to-one'
      : conversation.grouping === 'Channel'
      ? 'named-group'
      : 'adhoc-group';

  const participantCount = conversation.participants?.length ?? 0;

  return {
    conversationType,
    isActiveGroup,
    conversationName,
    participantCount,
  };
};

/**
 * Generates modal content for delete conversation confirmation
 * @param metadata - Conversation metadata
 * @returns Modal content with title, description and confirm text
 */
export const getDeleteConversationModalContent = (
  metadata: ConversationMetadata
): DeleteConversationModalContent => {
  const { conversationType, isActiveGroup, conversationName, participantCount } = metadata;

  switch (conversationType) {
    case 'one-to-one':
      return {
        title: 'Delete conversation?',
        description: `If you carry on, all message history with **${conversationName || 'this contact'}** will be deleted for you.`,
        confirmText: 'Delete',
      };

    case 'named-group':
      if (isActiveGroup) {
        return {
          title: 'Delete group conversation?',
          description: `If you carry on, you'll be removed from **${conversationName}** and lose access to all its history.`,
          confirmText: 'Leave and delete',
        };
      }
      return {
        title: 'Delete conversation?',
        description: `If you carry on, all message history with **${conversationName}** will be deleted for you.`,
        confirmText: 'Delete',
      };

    case 'adhoc-group':
      if (isActiveGroup && participantCount > MIN_PARTICIPANTS_TO_ALLOW_LEAVE_GROUP) {
        return {
          title: 'Delete group conversation?',
          description: "If you carry on, you'll be removed from this group and lose access to all its history.",
          confirmText: 'Leave and delete',
        };
      }
      return {
        title: 'Delete conversation?',
        description: `If you carry on, all message history ${conversationName ? `with **${conversationName}**` : 'from this group'} will be deleted for you.`,
        confirmText: 'Delete',
      };

    default:
      return {
        title: 'Delete conversation?',
        description: 'If you carry on, all message history will be deleted for you.',
        confirmText: 'Delete',
      };
  }
};